/**
 * Main-bar menus (MenuDef in contract.ts): which one is open, which view, what was picked, and what a
 * chip does. Pure functions over UiState.menu, so the rules are tested without React.
 */
import type { MenuChip, MenuDef, MenuEntry, MenuStep } from './contract';

/** The open menu: the action that opened it, its view, the views before (for Back), the picks. */
export interface OpenMenu {
  action: string;
  view: string;
  trail: string[];
  values: Record<string, string>;
}

export function openMenu(actionId: string, menu: MenuDef): OpenMenu {
  return { action: actionId, view: menu.start ?? 'main', trail: [], values: {} };
}

/** What a chip leads to: the menu in its next state, or closed (null) with something to do. */
export function stepMenu(menu: OpenMenu, step: MenuStep): { menu: OpenMenu | null; act: MenuStep | null } {
  if ('open' in step && !('set' in step)) {
    return { menu: { ...menu, view: step.open, trail: [...menu.trail, menu.view] }, act: null };
  }
  if ('set' in step) {
    const values = { ...menu.values, ...step.set };
    // a pick that opens a view moves on; one that goes back to where the menu came from returns
    if (step.open === undefined) return { menu: { ...menu, values }, act: null };
    const back = menu.trail.at(-1) === step.open;
    return {
      menu: { ...menu, values, view: step.open, trail: back ? menu.trail.slice(0, -1) : [...menu.trail, menu.view] },
      act: null,
    };
  }
  return { menu: null, act: step };
}

/** Back: the previous view, or closed from the first one. */
export function backMenu(menu: OpenMenu): OpenMenu | null {
  const previous = menu.trail.at(-1);
  return previous === undefined ? null : { ...menu, view: previous, trail: menu.trail.slice(0, -1) };
}

export const isChip = (e: MenuEntry): e is MenuChip => !('divider' in e);
